import React from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { UsersIcon, TrendingUpIcon, LandmarkIcon, RocketIcon } from '../Icons';

const About = () => {
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();

  const features = [
    {
      Icon: UsersIcon,
      title: language === 'tr' ? 'Siyasi İş Birliği' : 'Political Cooperation',
      description: language === 'tr'
        ? 'Türk devletleri arasında diplomatik bağların ve bölgesel istikrarın güçlendirilmesi.'
        : 'Strengthening diplomatic ties and regional stability among Turkic nations.',
    },
    {
      Icon: TrendingUpIcon,
      title: language === 'tr' ? 'Ekonomik Entegrasyon' : 'Economic Integration',
      description: language === 'tr'
        ? 'İpek Yolu boyunca ticaretin, yatırımların ve ekonomik koridorların geliştirilmesi.'
        : 'Facilitating trade, investment, and economic corridors across the Silk Road.',
    },
    {
      Icon: LandmarkIcon,
      title: language === 'tr' ? 'Kültürel Miras' : 'Cultural Heritage',
      description: language === 'tr'
        ? 'Ortak Türk geleneklerinin, dilinin ve tarihinin korunması ve tanıtılması.'
        : 'Preserving and promoting shared Turkic traditions, language, and history.',
    },
    {
      Icon: RocketIcon,
      title: language === 'tr' ? 'İnovasyon ve Teknoloji' : 'Innovation & Technology',
      description: language === 'tr'
        ? 'Dijital bağlantının ve teknolojik iş birliğinin ilerletilmesi.'
        : 'Advancing digital connectivity and technological cooperation.',
    },
  ];

  return (
    <section id="about" style={{
      ...styles.section,
      backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white,
    }}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={{
            ...styles.badge,
            backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : `${COLORS.primary}15`,
            color: isDarkMode ? DARK_THEME.primary : COLORS.primary,
          }}>{language === 'tr' ? 'Hakkımızda' : 'About Us'}</span>
          <h2 style={{...styles.title, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
            {language === 'tr' ? 'Türk Devletleri Teşkilatı' : 'Organization of Turkic States'}
          </h2>
          <p style={{...styles.description, color: isDarkMode ? DARK_THEME.textSecondary : '#666'}}>
            {language === 'tr'
              ? 'Türk Devletleri Teşkilatı, Türk ülkelerini bir araya getiren uluslararası bir teşkilattır. Türk dili konuşan devletler arasında kapsamlı iş birliğini geliştirmek amacıyla kurulan TDT; siyasi, ekonomik ve kültürel iş birliği için bir platform görevi görmektedir.'
              : `The Organization of Turkic States is an international organization comprising
            Turkic countries. Established to promote comprehensive cooperation among
            Turkic-speaking states, OTS serves as a platform for political, economic,
            and cultural collaboration.`}
          </p>
        </div>

        <div style={styles.features}>
          {features.map((feature, index) => (
            <div key={index} style={{
              ...styles.featureCard,
              backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.light,
              boxShadow: isDarkMode ? `0 4px 20px ${DARK_THEME.shadow}` : '0 4px 20px rgba(0,0,0,0.06)',
            }}>
              <div style={{
                ...styles.featureIcon,
                backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : `${COLORS.primary}12`,
                color: isDarkMode ? DARK_THEME.primary : COLORS.primary,
              }}>
                <feature.Icon size={28} />
              </div>
              <h3 style={{...styles.featureTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                {feature.title}
              </h3>
              <p style={{...styles.featureDescription, color: isDarkMode ? DARK_THEME.textSecondary : '#666'}}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <div style={{
          ...styles.vision,
          background: isDarkMode 
            ? `linear-gradient(135deg, ${DARK_THEME.primary} 0%, ${DARK_THEME.backgroundTertiary} 100%)`
            : COLORS.silkRoadGradient,
        }}>
          <div style={styles.visionContent}>
            <h3 style={styles.visionTitle}>{language === 'tr' ? '2040 Vizyonumuz' : 'Our Vision 2040'}</h3>
            <p style={styles.visionText}>
              {language === 'tr'
                ? '"Modern İpek Yolu boyunca birlik, iş birliği ve ortak refah yoluyla Türk Dünyasını bölgesel ve küresel meselelerde öncü bir güç hâline getirmek."'
                : `"To establish the Turkic World as a leading force in regional and global
              affairs through unity, cooperation, and shared prosperity along the
              modern Silk Road."`}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '100px 20px',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '60px',
  },
  badge: {
    display: 'inline-block',
    padding: '8px 20px',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '20px',
  },
  title: {
    fontSize: 'clamp(32px, 5vw, 48px)',
    marginBottom: '20px',
    fontWeight: '700',
  },
  description: {
    fontSize: '18px',
    lineHeight: '1.8',
    maxWidth: '800px',
    margin: '0 auto',
  },
  features: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '30px',
    marginBottom: '60px',
  },
  featureCard: {
    padding: '40px 30px',
    borderRadius: '16px',
    textAlign: 'center',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer',
  },
  featureIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  featureTitle: {
    fontSize: '20px',
    marginBottom: '12px',
    fontWeight: '600',
  },
  featureDescription: {
    fontSize: '15px',
    lineHeight: '1.6',
  },
  vision: {
    borderRadius: '20px',
    padding: '60px 40px',
    textAlign: 'center',
  },
  visionContent: {
    maxWidth: '700px',
    margin: '0 auto',
  },
  visionTitle: {
    color: COLORS.white,
    fontSize: '28px',
    marginBottom: '20px',
    fontWeight: '700',
  },
  visionText: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: '18px',
    lineHeight: '1.8',
    fontStyle: 'italic',
  },
};

export default About;
